// src/client/supabaseClient.ts
import { createClient as createSupabaseClient } from "@supabase/supabase-js";
var _client = null;
function createClient(url, anonKey) {
  if (!_client) {
    if (!url || !anonKey) {
      throw new Error(
        "Supabase URL and anon key are required. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env"
      );
    }
    _client = createSupabaseClient(url, anonKey);
  }
  return _client;
}
function getClient() {
  if (!_client) {
    throw new Error(
      "Supabase client not initialized. Call createClient() first."
    );
  }
  return _client;
}

// src/auth/SupabaseAuthProvider.ts
var SupabaseAuthProvider = class {
  async signInWithMagicLink(email, redirectTo) {
    const client = getClient();
    const { error } = await client.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectTo ?? window.location.origin
      }
    });
    if (error) {
      throw new Error(`Magic link sign-in failed: ${error.message}`);
    }
  }
  async signInWithGoogle() {
    const client = getClient();
    const { error } = await client.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) {
      throw new Error(`Google sign-in failed: ${error.message}`);
    }
  }
  async signOut() {
    const client = getClient();
    const { error } = await client.auth.signOut();
    if (error) {
      throw new Error(`Sign-out failed: ${error.message}`);
    }
  }
  async getSession() {
    const client = getClient();
    const { data, error } = await client.auth.getSession();
    if (error) {
      throw new Error(`Failed to get session: ${error.message}`);
    }
    if (!data.session) {
      return null;
    }
    const user = data.session.user;
    return {
      userId: user.id,
      email: user.email || ""
    };
  }
  onAuthStateChange(callback) {
    const client = getClient();
    const { data } = client.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        callback({
          userId: session.user.id,
          email: session.user.email || ""
        });
      } else {
        callback(null);
      }
    });
    return () => {
      data?.subscription?.unsubscribe();
    };
  }
};

// src/licensing/SupabaseLicenseProvider.ts
var LICENSE_STATUS_ACTIVE = "active";
var CHECKOUT_FUNCTION_NAME = "create-checkout";
var REDEEM_TOKEN_RPC = "redeem_license_token";
var SupabaseLicenseProvider = class {
  async checkLicense(userId) {
    const client = getClient();
    const result = await client.from("profiles").select("license_status, activated_at").eq("id", userId).single();
    if (result.error) {
      throw new Error(`Failed to check license: ${result.error.message}`);
    }
    const row = result.data;
    return {
      active: row?.license_status === LICENSE_STATUS_ACTIVE,
      activatedAt: row?.activated_at ?? null
    };
  }
  async redeemToken(token) {
    const client = getClient();
    const { data, error } = await client.rpc(REDEEM_TOKEN_RPC, {
      p_token: token
    });
    if (error) {
      throw new Error(`Failed to redeem token: ${error.message}`);
    }
    return { success: data === true };
  }
  async startCheckout(inviteCode) {
    const client = getClient();
    const { data, error } = await client.functions.invoke(CHECKOUT_FUNCTION_NAME, {
      body: { code: inviteCode }
    });
    if (error) {
      const checkoutError = new Error(
        data?.error ?? `Checkout creation failed: ${error.message}`
      );
      Object.assign(checkoutError, { checkoutErrorCode: data?.errorCode });
      throw checkoutError;
    }
    if (!data?.checkoutUrl) {
      throw new Error("No checkout URL returned from server");
    }
    return { checkoutUrl: data.checkoutUrl };
  }
};

// src/index.ts
function createSupabaseProviders(config) {
  createClient(config.url, config.anonKey);
  return {
    auth: new SupabaseAuthProvider(),
    license: new SupabaseLicenseProvider()
  };
}
export {
  SupabaseAuthProvider,
  SupabaseLicenseProvider,
  createSupabaseProviders,
  getClient
};
